/**
 * <SideNav> — 13-ui-screens.md §4.1, §3.7. BESPOKE, not antd `<Menu>`.
 *
 * RULED 2026-08-02 (README §4 conflict #20). `Menu` v6 removed the children
 * API, its group labels cannot take `.20em` letter-spacing without reaching
 * into antd's own group-title class, and a 3 px left accent plus a per-item
 * right slot is more fighting than writing. Because this is bespoke, the
 * selected state is our own `.cm-nav-item--selected` class, so no antd state
 * class is targeted anywhere in this codebase.
 *
 * THE RULE THAT MATTERS: a nav item the role may not use is SHOWN AND
 * DISABLED, with the literal word `locked` in its right slot — never hidden.
 * That is deliberate: the clerk learns the shape of the system.
 *
 * Items link through <NavAnchor>, not <Link>: the screens behind them are
 * built in parallel by other agents, so `routeTree.gen.ts` does not yet carry
 * every path and `<Link to=…>` would fail the build until the last one lands.
 * See NavAnchor.tsx for the full reasoning.
 */
import { NavAnchor } from './NavAnchor';
import type { CSSProperties } from 'react';

export interface NavItem {
  id: string;
  label: string;
  /** The public path — `/sheets`, `/workers`, … (no `_app` prefix). */
  path: string;
  /** `null` ⇒ render nothing. `0` also renders nothing (§4.1). */
  count: number | null;
  /** false ⇒ shown, `not-allowed`, right slot reads `locked`. */
  enabled: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export interface SideNavProps {
  groups: NavGroup[];
  /** The active item's `id`. Daily Job Entry keeps `sheets` active. */
  activeId: string | null;
  /** Deployed version + short git SHA, rendered in the footer. */
  build: string;
  /** The company in force. `resolveCompany().label` — never an empty string. */
  companyLabel: string;
}

const ITEM_BASE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '7px 18px',
  fontSize: 13,
  textDecoration: 'none',
};

const RIGHT_SLOT: CSSProperties = {
  marginLeft: 'auto',
  fontSize: 10,
  letterSpacing: '.06em',
  opacity: 0.8,
};

export function SideNav({ groups, activeId, build, companyLabel }: SideNavProps) {
  return (
    <nav
      style={{
        width: 'var(--cm-sider-w)',
        flex: '0 0 var(--cm-sider-w)',
        background: 'var(--brand-deep)',
        color: 'var(--green-200)',
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        height: 'var(--cm-viewport-h)',
      }}
    >
      <div style={{ padding: '18px 18px 16px', borderBottom: '1px solid var(--cm-sider-divider)' }}>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 800,
            fontSize: 14,
            letterSpacing: '.10em',
            color: 'var(--white)',
          }}
        >
          CROPMATE
        </div>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 10,
            letterSpacing: '.22em',
            color: 'var(--brand-accent)',
            marginTop: 5,
          }}
        >
          CONTRACT WAGES
        </div>
      </div>

      <div className="cm-scroll" style={{ flex: 1, overflowY: 'auto', padding: '12px 0 24px' }}>
        {groups.map((group) => (
          <div key={group.label} style={{ marginBottom: 14 }}>
            <div className="cm-nav-group">{group.label}</div>
            {group.items.map((item) => (
              <NavRow key={item.id} item={item} active={item.id === activeId} />
            ))}
          </div>
        ))}
      </div>

      <div
        style={{
          padding: '12px 18px 16px',
          borderTop: '1px solid var(--cm-sider-divider)',
          fontSize: 11,
          color: 'var(--green-300)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          {/* The AMBIENT identity slot: always visible, never interactive. The
              switcher in the top bar is where a company is CHANGED; this is where
              you check which one you are in without moving your eyes. */}
          <span>Company</span>
          <span style={{ color: 'var(--white)' }}>{companyLabel}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
          <span>Build</span>
          <span style={{ color: 'var(--white)', fontFamily: 'var(--font-mono)' }}>{build}</span>
        </div>
      </div>
    </nav>
  );
}

function NavRow({ item, active }: { item: NavItem; active: boolean }) {
  const cls = ['cm-nav-item', active && 'cm-nav-item--selected'].filter(Boolean).join(' ');
  /* Render nothing (not `0`) when a count is zero or the caller may not see it. */
  const right = item.enabled ? (item.count ? String(item.count) : '') : 'locked';

  if (!item.enabled) {
    return (
      <div
        className={cls}
        aria-disabled="true"
        title="Your role does not have access to this screen."
        style={{ ...ITEM_BASE, color: 'var(--cm-sider-disabled)', cursor: 'not-allowed' }}
      >
        <span>{item.label}</span>
        <span style={RIGHT_SLOT}>{right}</span>
      </div>
    );
  }

  return (
    <NavAnchor
      to={item.path}
      className={`${cls} cw-nav`}
      aria-current={active ? 'page' : undefined}
      style={{
        ...ITEM_BASE,
        color: active ? 'var(--white)' : 'var(--green-200)',
        fontWeight: active ? 600 : 400,
        background: active ? 'var(--cm-sider-selected)' : undefined,
      }}
    >
      <span>{item.label}</span>
      <span style={RIGHT_SLOT}>{right}</span>
    </NavAnchor>
  );
}
